
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getSupabaseConfig } from "@/utils/environmentUtils";

export const useAccessCodeDebugger = () => {
  const { toast } = useToast();

  const testAccessCodeGeneration = useMutation({
    mutationFn: async () => {
      console.log('Testing access code generation system...');
      
      const { data, error } = await supabase.functions.invoke('debug-access-codes', {
        body: { action: 'debug_generation' }
      });

      if (error) {
        console.error('Debug function error:', error);
        throw new Error(`Debug function failed: ${error.message}`);
      }

      console.log('Debug results:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('Access code debug completed successfully:', data);
      
      if (data.success && data.debug_results) {
        const summary = data.debug_results.summary;
        toast({
          title: "Access Code Generation Test Complete",
          description: `Processed ${summary.total_bookings_processed} bookings, generated ${summary.successfully_generated} codes, ${summary.errors} errors`,
          variant: summary.errors > 0 ? "destructive" : "default",
        });
      } else {
        toast({
          title: "Debug Test Failed",
          description: data.error || "Unknown error occurred",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error('Access code debug failed:', error);
      toast({
        title: "Debug Test Failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const testNewEdgeFunctionGeneration = useMutation({
    mutationFn: async () => {
      console.log('Testing new edge function access code generation...');
      
      const { data, error } = await supabase.functions.invoke('auto-generate-access-codes');

      if (error) {
        console.error('New edge function error:', error);
        throw new Error(`New edge function failed: ${error.message}`);
      }

      console.log('New edge function results:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('New edge function completed successfully:', data);
      
      if (data.success) {
        toast({
          title: "Edge Function Test Complete",
          description: `Processed ${data.bookings_processed} bookings, generated ${data.successfully_generated} codes, ${data.bookings_failed} failed`,
          variant: data.bookings_failed > 0 ? "destructive" : "default",
        });
      } else {
        toast({
          title: "Edge Function Test Failed",
          description: data.error || "Unknown error occurred",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error('New edge function test failed:', error);
      toast({
        title: "Edge Function Test Failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const testDirectCronCall = useMutation({
    mutationFn: async () => {
      console.log('Testing direct cron call to edge function...');
      
      // Get the Supabase configuration
      const config = getSupabaseConfig();
      
      // Make the same call that the cron job makes
      const response = await fetch(
        'https://doyqmjwlizoqfuabvluf.supabase.co/functions/v1/auto-generate-access-codes',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.anonKey}`,
          },
          body: JSON.stringify({
            source: 'direct_test',
            timestamp: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Direct cron call results:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('Direct cron call completed successfully:', data);
      
      if (data.success) {
        toast({
          title: "Direct Cron Call Successful",
          description: `Processed ${data.bookings_processed} bookings, generated ${data.successfully_generated} codes`,
          variant: "default",
        });
      } else {
        toast({
          title: "Direct Cron Call Failed",
          description: data.error || "Edge function returned error",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error('Direct cron call failed:', error);
      toast({
        title: "Direct Cron Call Failed",
        description: error.message || "Network or connection error",
        variant: "destructive",
      });
    },
  });

  return {
    testAccessCodeGeneration,
    testNewEdgeFunctionGeneration,
    testDirectCronCall,
    isTestingGeneration: testAccessCodeGeneration.isPending,
    isTestingNewEdgeFunction: testNewEdgeFunctionGeneration.isPending,
    isTestingDirectCronCall: testDirectCronCall.isPending,
  };
};
